import { Vector3 } from 'three/webgpu';

import { uNight, uWarm, uSunDir, uSunMix } from '../core/uniforms.js';
import { SUN_ARC, keyMix, sunDirAt } from '../bake/sunArc.js';
import { smooth, clamp, mod } from '../math/mathx.js';

/**
 * @file The clock. ONE object owns the time of day and pushes it into the two
 * global uniforms plus whatever visual systems are bound to it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ `sunDir` IS NOW THE REAL SUN. THIS USED TO SAY THE OPPOSITE. ⚠⚠
 * ─────────────────────────────────────────────────────────────────────────────
 * HISTORY, because the inversion matters: this file used to own a purely VISUAL
 * sun. The bake was frozen at one direction (`BakeConfig.sunDir`, ~42°), the
 * disc wandered independently, and the mismatch was invisible because nothing
 * cast a shadow to contradict it — the disc moved, the shading did not.
 *
 * `bake/sunKeys.js` deletes that licence. Visibility is now sampled at 29 sun
 * directions across the day and blended live, so a shadow on the ground WILL
 * disagree with a disc in the sky if the two ever diverge. Both therefore come
 * from the single arc in `bake/sunArc.js`, and this class's job is to push it
 * into `uSunDir` / `uSunMix` and to tell its owner when the resident key pair
 * changes.
 *
 * `BakeConfig.sunDir` still exists and is still the direction the ONE-SHOT bake
 * used — it is what an unkeyed geometry (a prop, a rig part, a car) is lit by,
 * and it is why those do not yet follow the sun. See `docs/rendering.md`.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Curves (from the plan; `el` = normalised sun elevation):
 *   el     = cos((h - 12)/12 * PI)          +1 at noon, -1 at midnight
 *   day    = smoothstep(-0.18, 0.26, el)
 *   night  = 1 - smoothstep(-0.28, 0.02, el)
 *   sunset = exp(-el^2 / 0.08)              golden band around the horizon
 *   uNight = night
 *   uWarm  = sunset * (1 - night) * 0.85    day channel only, so no third bake
 *
 * Wiring:
 *   engine.dayNight = dayNight;   // Engine calls update(dt) on the fixed step
 *   dayNight.bind({ sky, fog, post });
 *
 * @module engine/systems/DayNight
 */

/** @typedef {import('../render/sky.js').SkyHandle} SkyHandle */
/** @typedef {import('../render/fog.js').FogHandle} FogHandle */
/** @typedef {import('../render/post.js').PostHandle} PostHandle */

const HOURS_PER_DAY = 24;

export class DayNight {
  /**
   * @param {Object} [o]
   * @param {number} [o.startHour=13] 0..24.
   * @param {number} [o.dayLengthMinutes=8] Real minutes for a full 24h cycle.
   * @param {boolean} [o.playing=true] Start advancing immediately.
   * @param {number} [o.speed=1] Multiplier on top of `dayLengthMinutes`.
   * @param {import('../types.js').EventBus|null} [o.bus] Emits `'time'` at ~10Hz
   *   and on every discrete change (see {@link DayNight#snapshot}).
   * @param {typeof SUN_ARC} [o.arc] The sun arc. ⚠ MUST be the same arc the
   *   level's visibility keys were baked from — `bake/sunKeys.js` indexes keys
   *   by position in `sunArc.keyHours(arc)`, so a different arc here points the
   *   disc one way and the shadows another. Leave it alone unless you are also
   *   passing it to the bake.
   */
  constructor(o = {}) {
    const {
      startHour = 13,
      dayLengthMinutes = 8,
      playing = true,
      speed = 1,
      bus = null,
      arc = SUN_ARC,
    } = o;

    /** @type {number} 0..24 */
    this._hour = mod(startHour, HOURS_PER_DAY);
    /** @type {number} */
    this._dayLengthMinutes = Math.max(0.05, dayLengthMinutes);
    /** @type {boolean} */
    this._playing = playing;
    /** @type {number} */
    this._speed = speed;
    /** @type {typeof SUN_ARC} */
    this._arc = arc;

    /** @type {import('../types.js').EventBus|null} */
    this.bus = bus;
    /** @type {((s: ReturnType<DayNight['snapshot']>) => void)|null} Plain
     *  callback for consumers that do not want the bus (React HUD, editor). */
    this.onChange = null;
    /**
     * Fired when the RESIDENT visibility-key pair changes, i.e. when the clock
     * crosses a 30-minute key boundary. The level re-uploads `color3` from its
     * stored keys. Null in any scene without keys, which is every scene that is
     * not the city.
     * @type {((a: number, b: number) => void)|null}
     */
    this.onKeys = null;

    /** @type {{sky: SkyHandle|null, fog: FogHandle|null, post: PostHandle|null}} */
    this._bound = { sky: null, fog: null, post: null };

    // Derived, recomputed by _apply().
    this._el = 0;
    this._day = 0;
    this._night = 0;
    this._sunset = 0;
    /** @type {Vector3} Live vector — read it, never mutate it. */
    this._sunDir = new Vector3(0.42, 0.67, 0.61).normalize();
    /** Scratch for `sunArc.sunDirAt`, so the fixed step allocates nothing. */
    this._sunDirRaw = { x: 0, y: 0, z: 0 };
    /** Resident key pair. -1 forces `onKeys` to fire on the first `_apply`. */
    this._keyA = -1;
    this._keyB = -1;

    this._emitAccum = 0;
    this._disposed = false;

    this._apply(true);
  }

  // ── state ──────────────────────────────────────────────────────────────────

  /** @returns {number} 0..24 */
  get hour() {
    return this._hour;
  }

  set hour(h) {
    this.setHour(h);
  }

  /** @returns {number} 0 = full day, 1 = full night. Mirrors `uNight`. */
  get night() {
    return this._night;
  }

  /** @returns {number} 0..1 golden-hour grade. Mirrors `uWarm`. */
  get warm() {
    return uWarm.value;
  }

  /** @returns {number} 0..1 "is it daylight" — for gameplay/AI, not shading. */
  get day() {
    return this._day;
  }

  /** @returns {number} 0..1 raw golden-band curve (before the night rolloff). */
  get sunset() {
    return this._sunset;
  }

  /** @returns {number} -1..1 normalised sun elevation. */
  get elevation() {
    return this._el;
  }

  /**
   * VISUAL sun direction (surface -> sun). See the giant warning at the top of
   * this file: this is NOT the bake's sun.
   * @returns {Vector3} Live instance; treat as read-only.
   */
  get sunDir() {
    return this._sunDir;
  }

  /** @returns {boolean} */
  get playing() {
    return this._playing;
  }

  /** @returns {number} */
  get speed() {
    return this._speed;
  }

  /** @returns {number} Real minutes per in-game day. */
  get dayLengthMinutes() {
    return this._dayLengthMinutes;
  }

  // ── control ────────────────────────────────────────────────────────────────

  /**
   * @param {number} h Hours; wrapped into 0..24, so `setHour(-1)` is 23:00.
   * @returns {this}
   */
  setHour(h) {
    this._hour = mod(Number(h) || 0, HOURS_PER_DAY);
    this._apply(true);
    return this;
  }

  /**
   * @param {number} mult Multiplier on the base cycle rate. 1 = the configured
   *   `dayLengthMinutes`; 16 is a good "watch it spin" debug speed.
   * @returns {this}
   */
  setSpeed(mult) {
    this._speed = Math.max(0, Number(mult) || 0);
    this._emit(true);
    return this;
  }

  /**
   * @param {number} minutes Real minutes per in-game day.
   * @returns {this}
   */
  setDayLength(minutes) {
    this._dayLengthMinutes = Math.max(0.05, Number(minutes) || 8);
    this._emit(true);
    return this;
  }

  /** @returns {this} */
  pause() {
    this._playing = false;
    this._emit(true);
    return this;
  }

  /** @returns {this} */
  play() {
    this._playing = true;
    this._emit(true);
    return this;
  }

  /**
   * @param {boolean} [on] Omit to toggle.
   * @returns {this}
   */
  togglePlay(on) {
    return (on ?? !this._playing) ? this.play() : this.pause();
  }

  // ── binding ────────────────────────────────────────────────────────────────

  /**
   * Attaches visual systems. **MERGE semantics** — keys you omit keep their
   * current binding, so a later owner (T11's `<TimeOfDay />`, the editor, a
   * gallery route) can re-bind ONE target without knowing about the others:
   *
   *     dayNight.bind({ post: myPost });        // sky + fog untouched
   *     dayNight.bind({ sky: null });           // explicit null unbinds
   *
   * Every bind applies the current time immediately, so a freshly created sky is
   * never one frame out of date (and never flashes noon-blue at 22:00).
   *
   * Duck-typed on purpose — anything with the right method works:
   *   `sky.setTime({ night, warm, sunset, day, sunDir })`
   *   `fog.setTime({ night, warm, sunset, day })`
   *   `post.setNight(night)`
   *
   * @param {{sky?: SkyHandle|null, fog?: FogHandle|null, post?: PostHandle|null}} targets
   * @returns {this}
   */
  bind(targets = {}) {
    if ('sky' in targets) this._bound.sky = targets.sky ?? null;
    if ('fog' in targets) this._bound.fog = targets.fog ?? null;
    if ('post' in targets) this._bound.post = targets.post ?? null;
    this._apply(true);
    return this;
  }

  /**
   * @param {'sky'|'fog'|'post'} [what] Omit to unbind everything.
   * @returns {this}
   */
  unbind(what) {
    if (what) this._bound[what] = null;
    else this._bound = { sky: null, fog: null, post: null };
    return this;
  }

  // ── tick ───────────────────────────────────────────────────────────────────

  /**
   * Called by `Engine._fixed()` at the fixed step when assigned to
   * `engine.dayNight`.
   * @param {number} dt Seconds.
   */
  update(dt) {
    if (this._disposed) return;
    if (this._playing && this._speed > 0) {
      const hoursPerSecond = HOURS_PER_DAY / (this._dayLengthMinutes * 60);
      this._hour = mod(this._hour + dt * hoursPerSecond * this._speed, HOURS_PER_DAY);
      this._apply(false);
    }
    this._emitAccum += dt;
    if (this._emitAccum >= 0.1) this._emit(false);
  }

  /**
   * The full state, for HUDs and debug overlays.
   * @returns {{hour: number, night: number, warm: number, day: number,
   *   sunset: number, elevation: number, playing: boolean, speed: number,
   *   dayLengthMinutes: number, sunDir: {x: number, y: number, z: number},
   *   label: string}}
   */
  snapshot() {
    return {
      hour: this._hour,
      night: this._night,
      warm: uWarm.value,
      day: this._day,
      sunset: this._sunset,
      elevation: this._el,
      playing: this._playing,
      speed: this._speed,
      dayLengthMinutes: this._dayLengthMinutes,
      sunDir: { x: this._sunDir.x, y: this._sunDir.y, z: this._sunDir.z },
      label: formatHour(this._hour),
    };
  }

  /** Resets the uniforms to noon and drops every binding. Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.unbind();
    this.onChange = null;
    this.onKeys = null;
    this.bus = null;
    uNight.value = 0;
    uWarm.value = 0;
    uSunMix.value = 0;
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /**
   * Recomputes the curves, writes the uniforms, pushes to bound systems.
   * @param {boolean} forceEmit
   */
  _apply(forceEmit) {
    const h = this._hour;

    const el = Math.cos(((h - 12) / 12) * Math.PI);
    const day = smooth(-0.18, 0.26, el);
    const night = 1 - smooth(-0.28, 0.02, el);
    const sunset = Math.exp(-(el * el) / 0.08);

    this._el = el;
    this._day = day;
    this._night = night;
    this._sunset = sunset;

    uNight.value = clamp(night, 0, 1);
    // The day channel is the only one graded, and only while it is still
    // visible: `* (1 - night)` stops a 3am moon from looking like a campfire.
    uWarm.value = clamp(sunset * (1 - night) * 0.85, 0, 1);

    // ── the sun ──
    // ONE arc, shared with the bake's keys. `_el` above is deliberately NOT
    // derived from it: that curve drives uNight/uWarm/the sky stops and has its
    // own tuned thresholds, and re-deriving it here would move the weather and
    // sky tests for no gain. Only the DIRECTION comes from `sunArc`.
    sunDirAt(h, this._arc, this._sunDirRaw);
    this._sunDir.set(this._sunDirRaw.x, this._sunDirRaw.y, this._sunDirRaw.z);
    uSunDir.value.copy(this._sunDir);

    const mix = keyMix(h, this._arc);
    uSunMix.value = mix.f;
    if (mix.a !== this._keyA || mix.b !== this._keyB) {
      this._keyA = mix.a;
      this._keyB = mix.b;
      // The owner re-uploads the two resident visibility channels. Fired only on
      // a CHANGE — at 8 real minutes per day a key boundary comes round about
      // every 10 seconds, and re-packing every chunk every frame would be absurd.
      this.onKeys?.(mix.a, mix.b);
    }

    const payload = {
      night: this._night,
      warm: uWarm.value,
      sunset,
      day,
      sunDir: this._sunDir,
    };

    this._bound.sky?.setTime?.(payload);
    this._bound.fog?.setTime?.(payload);
    this._bound.post?.setNight?.(this._night);

    if (forceEmit) this._emit(true);
  }

  /** @param {boolean} force */
  _emit(force) {
    if (!force && this._emitAccum < 0.1) return;
    this._emitAccum = 0;
    if (!this.bus && !this.onChange) return;
    const snap = this.snapshot();
    this.bus?.emit?.('time', snap);
    this.onChange?.(snap);
  }
}

/**
 * `13.75` -> `'13:45'`.
 * @param {number} h
 * @returns {string}
 */
export function formatHour(h) {
  const t = mod(h, HOURS_PER_DAY);
  const hh = Math.floor(t);
  const mm = Math.floor((t - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** Deep-link presets the HUD and the dev scene share. */
export const TIME_PRESETS = Object.freeze([
  { id: 'dawn', label: 'dawn', hour: 6.5 },
  { id: 'noon', label: 'noon', hour: 12 },
  { id: 'dusk', label: 'dusk', hour: 18.25 },
  { id: 'night', label: 'night', hour: 22 },
]);

/**
 * Reads the `?hour=` deep link. Accepts `6.5`, `6:30` or a preset id.
 * @param {string|URLSearchParams|null} [search] Defaults to `location.search`.
 * @returns {number|null} Hours, or null when absent/unparseable.
 */
export function hourFromQuery(search) {
  let params;
  if (search instanceof URLSearchParams) params = search;
  else if (typeof search === 'string') params = new URLSearchParams(search);
  else if (typeof window !== 'undefined') params = new URLSearchParams(window.location.search);
  else return null;

  const raw = params.get('hour');
  if (raw == null || raw === '') return null;

  const preset = TIME_PRESETS.find((p) => p.id === raw.toLowerCase());
  if (preset) return preset.hour;

  if (raw.includes(':')) {
    const [a, b] = raw.split(':');
    const hh = Number(a);
    const mm = Number(b);
    if (Number.isFinite(hh) && Number.isFinite(mm)) return mod(hh + mm / 60, HOURS_PER_DAY);
    return null;
  }

  const n = Number(raw);
  return Number.isFinite(n) ? mod(n, HOURS_PER_DAY) : null;
}
